import { create } from 'zustand';
import qs from 'qs';
import { BlogPost, BlogComment, CreateBlogData, UpdateBlogData } from '@/types';
import { validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';

const baseUrl = '/api/strapi';

interface BlogResponse {
  data: BlogPost[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface BlogCommentsResponse {
  data: BlogComment[];
}

interface CreateCommentParams {
  blogDocumentId: string;
  content: string;
  parentCommentDocumentId?: string;
}

interface BlogState {
  blogs: BlogPost[];
  featuredBlog: BlogPost | null;
  commentsByBlog: Record<string, BlogComment[]>;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  /** Category slug, or 'all' for no filter. */
  selectedCategory: string | 'all';
  searchQuery: string;

  fetchBlogs: (page?: number, category?: string | 'all', search?: string, includeDrafts?: boolean) => Promise<void>;
  fetchFeaturedBlog: () => Promise<void>;
  fetchComments: (blogDocumentId: string) => Promise<void>;
  createComment: (params: CreateCommentParams) => Promise<boolean>;
  updateComment: (blogDocumentId: string, commentDocumentId: string, content: string) => Promise<boolean>;
  deleteComment: (blogDocumentId: string, commentDocumentId: string) => Promise<boolean>;
  createBlog: (data: CreateBlogData) => Promise<boolean>;
  updateBlog: (documentId: string, data: UpdateBlogData) => Promise<boolean>;
  deleteBlog: (documentId: string) => Promise<boolean>;
  toggleLike: (blogDocumentId: string) => Promise<void>;
  toggleCommentLike: (blogDocumentId: string, commentDocumentId: string) => Promise<void>;
  trackView: (blogDocumentId: string) => Promise<void>;
  hasUserLiked: (blogDocumentId: string, userId: number) => boolean;
  hasUserLikedComment: (blogDocumentId: string, commentDocumentId: string, userId: number) => boolean;
  setSelectedCategory: (category: string | 'all', includeDrafts?: boolean) => void;
  setSearchQuery: (query: string, includeDrafts?: boolean) => void;
  getPopularBlogs: () => BlogPost[];
}

export const useBlogStore = create<BlogState>((set, get) => ({
  blogs: [],
  featuredBlog: null,
  commentsByBlog: {},
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  hasMore: true,
  selectedCategory: 'all',
  searchQuery: '',

  fetchBlogs: async (page = 1, category?: string | 'all', search?: string, includeDrafts = false) => {
    try {
      set({ loading: true, error: null });

      // Base filter: exclude deleted posts
      const filters: Record<string, unknown> = { deletedAt: { $null: true } };

      const currentCategory = category !== undefined ? category : get().selectedCategory;
      const currentSearch = search !== undefined ? search : get().searchQuery;

      // The featured card only renders on the unfiltered, unsearched view (see blog/page.tsx),
      // so a featured published post must stay IN this list whenever a category or search
      // filter is active — otherwise it's excluded from featured section AND the list.
      const isDefaultView = currentCategory === 'all' && !currentSearch;

      // Hide drafts unless explicitly included (for MWBI admins)
      if (!includeDrafts) {
        filters.isDraft = { $ne: true };
        if (isDefaultView) {
          // Exclude featured published blogs (they show in featured section)
          filters.featured = { $ne: true };
        }
      } else if (isDefaultView) {
        // For admins on the default view: show all blogs, but:
        // - non-featured blogs always appear
        // - featured blogs that are drafts also appear (since they don't show in featured section)
        // - featured published blogs don't appear (they show in featured section)
        filters.$or = [
          { featured: { $ne: true } },
          { $and: [{ featured: { $eq: true } }, { isDraft: { $eq: true } }] }
        ];
      }
      // else (admin + category/search filter active): no featured-based exclusion —
      // all matching blogs (drafts included) appear in the filtered list.

      if (currentCategory && currentCategory !== 'all') {
        filters.categories = { slug: { $eq: currentCategory } };
      }

      if (currentSearch) {
        // Wrap existing $or in $and if it exists, then add search $or
        const searchFilter = {
          $or: [
            { title: { $containsi: currentSearch } },
            { description: { $containsi: currentSearch } },
            { content: { $containsi: currentSearch } },
            // MWBI-900: without the Spanish columns, a Spanish visitor searching words
            // they can plainly see on the page would get zero results. Six short clauses
            // stay far inside the 2048-byte query-string ceiling AWS WAF enforces.
            { titleEs: { $containsi: currentSearch } },
            { descriptionEs: { $containsi: currentSearch } },
            { contentEs: { $containsi: currentSearch } },
          ]
        };

        if (filters.$or) {
          // Combine both $or conditions with $and
          const existingOr = filters.$or;
          delete filters.$or;
          filters.$and = [
            { $or: existingOr as unknown[] },
            searchFilter
          ];
        } else {
          filters.$or = searchFilter.$or;
        }
      }

      const query = {
        populate: {
          coverImage: { fields: ['url', 'alternativeText'] },
          author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          categories: { fields: ['id', 'documentId', 'name', 'slug', 'archived'] },
          liked_by: { fields: ['id'] },
          viewed_by: { fields: ['id'] },
          blog_comments: { count: true },
        },
        sort: ['createdAt:desc'],
        filters,
        pagination: { page, pageSize: 12 },
      };

      const queryString = qs.stringify(query, { encodeValuesOnly: true });
      // eslint-disable-next-line local/no-unpaginated-strapi-fetch -- intentional page-by-page blog list pagination (load-more UI)
      const res = await fetch(`${baseUrl}/blog-posts?${queryString}`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch blogs');

      const data: BlogResponse = await res.json();

      set((state) => ({
        blogs: page === 1 ? data.data : [...state.blogs, ...data.data],
        currentPage: page,
        totalPages: data.meta.pagination.pageCount,
        hasMore: page < data.meta.pagination.pageCount,
        loading: false,
      }));
    } catch (err) {
      console.error('blogStore.fetchBlogs failed', err);
      set({
        error: err instanceof Error ? err.message : 'Error fetching blogs',
        loading: false
      });
    }
  },

  fetchFeaturedBlog: async () => {
    try {
      const query = {
        populate: {
          coverImage: { fields: ['url', 'alternativeText'] },
          author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          categories: { fields: ['id', 'documentId', 'name', 'slug', 'archived'] },
          liked_by: { fields: ['id'] },
          viewed_by: { fields: ['id'] },
        },
        filters: {
          featured: { $eq: true },
          deletedAt: { $null: true },
          isDraft: { $ne: true }  // Exclude drafts from featured
        },
        sort: ['createdAt:desc'],
        pagination: { page: 1, pageSize: 1 },
      };

      const queryString = qs.stringify(query, { encodeValuesOnly: true });
      // eslint-disable-next-line local/no-unpaginated-strapi-fetch -- single featured post (pageSize 1)
      const res = await fetch(`${baseUrl}/blog-posts?${queryString}`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch featured blog');

      const data: BlogResponse = await res.json();

      set({ featuredBlog: data.data[0] || null });
    } catch (err) {
      console.error('Error fetching featured blog:', err);
    }
  },

  fetchComments: async (blogDocumentId: string) => {
    try {
      const comments = await fetchStrapiCollection<BlogCommentsResponse['data'][number]>('blog-comments', {
        filters: { blog: { documentId: blogDocumentId }, deletedAt: { $null: true } },
        populate: {
          author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          liked_by: { fields: ['id'] },
          parentComment: { fields: ['id', 'documentId'] },
        },
        sort: ['createdAt:asc'],
      });
      set((state) => ({
        commentsByBlog: { ...state.commentsByBlog, [blogDocumentId]: comments }
      }));
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  },

  createComment: async (params: CreateCommentParams) => {
    try {
      const res = await fetch(`${baseUrl}/blog-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content: params.content,
            blog: { connect: [params.blogDocumentId] },
            parentComment: params.parentCommentDocumentId
              ? { connect: [params.parentCommentDocumentId] }
              : null,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to create comment');

      await get().fetchComments(params.blogDocumentId);
      return true;
    } catch (err) {
      console.error('Error creating comment:', err);
      return false;
    }
  },

  updateComment: async (blogDocumentId: string, commentDocumentId: string, content: string) => {
    try {
      const res = await fetch(`${baseUrl}/blog-comments/${commentDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            content,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to update comment');

      await get().fetchComments(blogDocumentId);
      return true;
    } catch (err) {
      console.error('Error updating comment:', err);
      return false;
    }
  },

  deleteComment: async (blogDocumentId: string, commentDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/blog-comments/${commentDocumentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            deletedAt: new Date().toISOString(),
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to delete comment');

      await get().fetchComments(blogDocumentId);
      return true;
    } catch (err) {
      console.error('Error deleting comment:', err);
      return false;
    }
  },

  createBlog: async (data: CreateBlogData) => {
    try {
      set({ loading: true, error: null });

      // Upload cover image if provided
      let coverImageId: number | null = null;
      if (data.coverImage) {
        validateFileSize(data.coverImage);

        const formData = new FormData();
        formData.append('files', data.coverImage);
        const uploadRes = await fetch(`${baseUrl}/upload`, {
          method: 'POST',
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadedFiles = await uploadRes.json();
          coverImageId = uploadedFiles[0]?.id ?? null;
        }
      }

      const blogData: Record<string, unknown> = {
        title: data.title,
        description: data.description,
        content: data.content,
        youtubeUrl: data.youtubeUrl,
        categories: { connect: data.categoryDocumentIds },
        readTime: data.readTime,
        featured: data.featured || false,
        isDraft: data.isDraft || false,
        views: 0,
        likes: 0,
      };

      if (coverImageId) {
        blogData.coverImage = coverImageId;
      }

      const res = await fetch(`${baseUrl}/blog-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: blogData }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData?.error?.message || 'Failed to create blog post');
      }

      // Refresh blogs list and featured blog
      await get().fetchBlogs(1);
      await get().fetchFeaturedBlog();
      set({ loading: false });
      return true;
    } catch (err) {
      console.error('Error creating blog:', err);
      set({ error: (err as Error).message, loading: false });
      return false;
    }
  },

  updateBlog: async (documentId: string, data: UpdateBlogData) => {
    try {
      // Upload cover image if provided
      let coverImageId: number | null = null;
      if (data.coverImage) {
        validateFileSize(data.coverImage);

        const formData = new FormData();
        formData.append('files', data.coverImage);
        const uploadRes = await fetch(`${baseUrl}/upload`, {
          method: 'POST',
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadedFiles = await uploadRes.json();
          coverImageId = uploadedFiles[0]?.id ?? null;
        }
      }

      const blogData: Record<string, unknown> = {};
      if (data.title !== undefined) blogData.title = data.title;
      if (data.description !== undefined) blogData.description = data.description;
      if (data.content !== undefined) blogData.content = data.content;
      if (data.youtubeUrl !== undefined) blogData.youtubeUrl = data.youtubeUrl;
      if (data.categoryDocumentIds !== undefined) {
        // `set` (not `connect`) — connect only adds, so removing a category
        // on edit would be silently impossible if this used connect.
        blogData.categories = { set: data.categoryDocumentIds };
      }
      if (data.featured !== undefined) blogData.featured = data.featured;
      if (data.isDraft !== undefined) blogData.isDraft = data.isDraft;
      if (coverImageId) blogData.coverImage = coverImageId;
      // MWBI-900. This builder copies an explicit allowlist rather than spreading, so a
      // new field is dropped silently unless it is named here — which is exactly how a
      // hand-edited Spanish title was lost client-side before it ever reached Strapi.
      // Each is sent only when the admin actually edited it; echoing back untouched
      // Spanish would diverge the stored fingerprint and freeze the item's translation.
      if (data.titleEs !== undefined) blogData.titleEs = data.titleEs;
      if (data.descriptionEs !== undefined) blogData.descriptionEs = data.descriptionEs;
      if (data.contentEs !== undefined) blogData.contentEs = data.contentEs;
      if (data.translationLocked !== undefined) blogData.translationLocked = data.translationLocked;

      const res = await fetch(`${baseUrl}/blog-posts/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: blogData }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData?.error?.message || 'Failed to update blog post');
      }

      // Refresh blogs list
      await get().fetchBlogs(1);
      await get().fetchFeaturedBlog();
      return true;
    } catch (err) {
      console.error('Error updating blog:', err);
      set({ error: (err as Error).message });
      return false;
    }
  },

  deleteBlog: async (documentId: string) => {
    try {
      // Soft delete by setting deletedAt
      const res = await fetch(`${baseUrl}/blog-posts/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { deletedAt: new Date().toISOString() } }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData?.error?.message || 'Failed to delete blog post');
      }

      // Refresh blogs list
      await get().fetchBlogs(1);
      await get().fetchFeaturedBlog();
      return true;
    } catch (err) {
      console.error('Error deleting blog:', err);
      return false;
    }
  },

  toggleLike: async (blogDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/blog-posts/${blogDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle like');

      const { data } = await res.json();

      set((state) => ({
        blogs: state.blogs.map(b =>
          b.documentId === blogDocumentId
            ? {
                ...b,
                likes: data.likeCount,
                liked_by: data.liked_by ?? b.liked_by,
              }
            : b
        ),
        featuredBlog: state.featuredBlog?.documentId === blogDocumentId
          ? {
              ...state.featuredBlog,
              likes: data.likeCount,
              liked_by: data.liked_by ?? state.featuredBlog.liked_by,
            }
          : state.featuredBlog,
      }));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  },

  toggleCommentLike: async (blogDocumentId: string, commentDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/blog-comments/${commentDocumentId}/toggle-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle comment like');

      const { data } = await res.json();

      set((state) => ({
        ...(state.commentsByBlog[blogDocumentId]
          ? {
              commentsByBlog: {
                ...state.commentsByBlog,
                [blogDocumentId]: state.commentsByBlog[blogDocumentId].map(c =>
                  c.documentId === commentDocumentId
                    ? {
                        ...c,
                        likes: data.likeCount,
                        liked_by: data.liked_by ?? c.liked_by,
                      }
                    : c
                ),
              },
            }
          : {}),
      }));
    } catch (err) {
      console.error('Error toggling comment like:', err);
    }
  },

  trackView: async (blogDocumentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/blog-posts/${blogDocumentId}/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to track view');

      const { data } = await res.json();

      set((state) => ({
        blogs: state.blogs.map(b =>
          b.documentId === blogDocumentId
            ? { ...b, views: data.viewCount }
            : b
        ),
        featuredBlog: state.featuredBlog?.documentId === blogDocumentId
          ? { ...state.featuredBlog, views: data.viewCount }
          : state.featuredBlog,
      }));
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  },

  hasUserLiked: (blogDocumentId: string, userId: number) => {
    const blog = get().blogs.find(b => b.documentId === blogDocumentId);
    return blog?.liked_by?.some(u => u.id === userId) ?? false;
  },

  hasUserLikedComment: (blogDocumentId: string, commentDocumentId: string, userId: number) => {
    const comments = get().commentsByBlog[blogDocumentId] || [];
    const comment = comments.find(c => c.documentId === commentDocumentId);
    return comment?.liked_by?.some(u => u.id === userId) ?? false;
  },

  setSelectedCategory: (category: string | 'all', includeDrafts = false) => {
    set({ selectedCategory: category });
    get().fetchBlogs(1, category, undefined, includeDrafts);
  },

  setSearchQuery: (query: string, includeDrafts = false) => {
    set({ searchQuery: query });
    get().fetchBlogs(1, undefined, query, includeDrafts);
  },

  getPopularBlogs: () => {
    const blogs = get().blogs;
    return [...blogs]
      .sort((a, b) => b.views - a.views)
      .slice(0, 3);
  },
}));
